import React from 'react';
import Card from '../../components/card';
import SessionCard from '../../components/card/session-card';
import { InfoCopy, InfoLink } from '../../components/info-link';
import parseName from '../../modules/parse-name';
import { Authorization } from './authorization';
import { PersonalInformation } from './information';

/**
 * ScenarioType = 'Affordability' | 'Preapproval' | 'RefiFull' |
 * 'RefiQualify' | 'Qualify' | 'Purchase';
 *
 * @param { ScenarioType } scenario
 * @param { boolean } coborrower Whether a coborrower exists on the questionnaire.
 */
const getTitle = (scenario, coborrower) => {
  switch (scenario) {
    case 'Affordability':
      return `To get you an accurate affordability estimate we’d like to review your credit using a soft inquiry. 
      This will not change your credit score.`;
    case 'Preapproval':
      return `To get you pre-approved we’d like to review your credit using a soft inquiry.
      This will not change your credit score.`;
    case 'Purchase':
      return `To get you accurate rates we’d like to review your credit using a soft inquiry. 
      This will not change your credit score.`;
    case 'RefiFull':
    case 'RefiQualify':
      return `To get you accurate rates we’d like to review your credit using a soft inquiry.
      This will not change your credit score.`;
    default:
      return `To best serve you, we need to retrieve your credit ${
        coborrower ? 'scores' : 'score'
      }.`;
  }
};

/**
 * @param { string } name
 */
export const validateName = (name) => {
  const { first, last } = parseName(name);
  return !!(first && last);
};

/**
 * Returns true if the age is greater than 15 years ago as of today 11:59:59.
 * Otherwise, returns false.
 * @param { number } dateOfBirth
 */
export const validateAge = (dateOfBirth) => {
  if (dateOfBirth == null) {
    return false;
  }
  const cutOff = new Date();
  cutOff.setFullYear(cutOff.getFullYear() - 15);
  cutOff.setHours(23, 59, 59);
  return cutOff.getTime() > dateOfBirth;
};

const Info = (props) => (
  <div>
    <InfoLink title="What's a soft credit inquiry?" {...props}>
      <InfoCopy>
        A soft credit inquiry produces the exact same information that banks and other lenders use
        in the application process, without the negative impact to your credit score that comes from
        a “hard” inquiry. Our soft inquiry checks your &quot;FICO 2&quot; score from Experian and
        your “FICO 4” score from Transunion, which are different than the credit scores offered by
        free credit reporting services. You can read more about different credit reports and how
        they’re used,{' '}
        <a
          href="https://resources.ownup.com/the-credit-score-conundrum"
          className="link__hover--yellow"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>
        .
      </InfoCopy>
    </InfoLink>
    <InfoLink title="Why we use a soft credit inquiry" {...props}>
      <InfoCopy>
        Checking your credit via soft inquiry helps us better forecast how each lender will assess
        your creditworthiness, so we can get you accurate rates and terms. Additionally,
        understanding your credit helps us personalize our guidance about how much you can qualify
        for, and what lenders and products are the best fit for you.
      </InfoCopy>
    </InfoLink>
  </div>
);

/**
 * @param model A representation of the state (see: stateContainer). It is an
 *   Immutable Map with the following keys: borrower, loan, coborrower.
 * @param { ScenarioType } scenario
 * @param onChange Function with the following parameters:
 *   @param modelKey Root-level key on the ImmutableJS model
 *            (i.e., "borrower", "loan", "coborrower")
 *   @param key Key on the second level (e.g. "creditAgree" on loan)
 *   @param val The new value for the key.
 */
const CreditAuthorization = ({ model, onChange, scenario, ...cardProps }) => (
  <Card
    title={getTitle(scenario, model.getIn(['loan', 'hasCoborrower']))}
    info={Info}
    {...cardProps}
  >
    <PersonalInformation {...{ model, onChange }} />
    <br />
    <Authorization {...{ model, onChange }} />
  </Card>
);

const Wrapper = (Card, id, completed) => {
  const Wrapped = ({ model, onChange, ...wrappedProps }) => (
    <Card
      {...wrappedProps}
      model={model}
      onChange={onChange}
      complete={!!Wrapped.completed({ model })}
      scenario={model.getIn(['loan', 'scenario'])}
    />
  );

  Wrapped.completed = completed;
  Wrapped.id = id;

  return Wrapped;
};

const validatePrimaryBorrower = ({ model }) => {
  const borrower = model.get('borrower');

  return (
    validateName(borrower.get('name')) &&
    validateAge(borrower.get('dateOfBirth')) &&
    borrower.get('address') &&
    borrower.get('city') &&
    borrower.get('state') &&
    borrower.get('zip')
  );
};

const primaryBorrowerCompleted = ({ model }) => {
  const loan = model.get('loan');

  return (
    !loan.get('hasCoborrower') &&
    !!loan.get('creditAgree') &&
    validatePrimaryBorrower({ id: 'borrower', model })
  );
};

const withCoborrowerCompleted = ({ model }) => {
  const loan = model.get('loan');
  const coborrower = model.get('coborrower');

  return (
    !!loan.get('hasCoborrower') &&
    !!loan.get('creditAgree') &&
    validatePrimaryBorrower({ model }) &&
    validateName(coborrower.get('name')) &&
    (coborrower.get('cohabitate') ||
      (coborrower.get('address') &&
        coborrower.get('city') &&
        coborrower.get('state') &&
        coborrower.get('zip')))
  );
};

export const SingleBorrowerCreditAuthorization = SessionCard(
  Wrapper(CreditAuthorization, 'SingleBorrowerCreditAuthorization', primaryBorrowerCompleted)
);

export const WithCoborrowerCreditAuthorization = SessionCard(
  Wrapper(CreditAuthorization, 'WithCoborrowerCreditAuthorization', withCoborrowerCompleted)
);



// WEBPACK FOOTER //
// ./src/cards/credit-authorization/index.js